import React, { useEffect, useState } from "react";
import client from "../lib/api";
import { Card, Select, Button, Badge } from "./ui";
import { LANGS } from "../i18n";
import { downloadSpecialManual } from "../lib/manuals";
import { BookOpen, FileText, ShieldCheck, Eye, X, Download } from "lucide-react";

const LANG_NATIVE = Object.fromEntries(LANGS.map((l) => [l.code, l.native]));

function ManualBody({ data }) {
  const dir = data.rtl ? "rtl" : "ltr";
  return (
    <div dir={dir} className="space-y-5" data-testid="special-preview-content">
      <div className="border-b border-black/10 dark:border-white/10 pb-3">
        <h3 className="font-head text-xl">{data.title}</h3>
        <p className="text-sm opacity-70">{data.subtitle}</p>
        <p className="text-xs opacity-50 mt-1">
          {LANG_NATIVE[data.lang] || data.lang} ({data.lang}) · Version {data.version} · Last modified {(data.last_modified || "").slice(0, 10)}
        </p>
      </div>

      {/* Contents */}
      <div>
        <div className="text-sm font-semibold text-[var(--primary)] mb-1">Contents</div>
        <ol className="text-sm space-y-0.5 opacity-80">
          {data.sections.map((s, i) => <li key={i}>{i + 1}.&nbsp;&nbsp;{s.title}</li>)}
        </ol>
      </div>

      {data.sections.map((s, i) => {
        const isIndex = i === data.sections.length - 1;
        return (
          <section key={i} className="space-y-2">
            <h4 className="font-head text-base text-[var(--primary)]">{i + 1}. {s.title}</h4>
            {isIndex ? (
              <ul className="text-sm columns-1 sm:columns-2 gap-6">
                {data.index.map(([subj, sec], k) => (
                  <li key={k} className="break-inside-avoid">{subj} — §{sec}</li>
                ))}
              </ul>
            ) : (
              <>
                {(s.body || "").split("\n\n").map((p, k) => (
                  <p key={k} className="text-sm leading-relaxed opacity-90 whitespace-pre-line">{p}</p>
                ))}
                {s.table && s.table.rows && (
                  <div className="mt-2 overflow-x-auto">
                    {s.table.caption && <div className="text-xs font-medium opacity-70 mb-1">{s.table.caption}</div>}
                    <table className="w-full text-xs border border-black/10 dark:border-white/10">
                      <thead>
                        <tr className="bg-[var(--primary)] text-white">
                          {s.table.cols.map((c, k) => <th key={k} className="text-start p-2 font-medium">{c}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {s.table.rows.map((r, ri) => (
                          <tr key={ri} className={ri % 2 ? "bg-black/[0.03] dark:bg-white/[0.04]" : ""}>
                            {r.map((c, ci) => <td key={ci} className="align-top p-2 border-t border-black/5 dark:border-white/5">{c}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </section>
        );
      })}
    </div>
  );
}

export default function SpecialManuals() {
  const [manuals, setManuals] = useState([]);
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null); // resolved JSON

  useEffect(() => {
    client.get("/manual/special")
      .then((r) => {
        setManuals(r.data || []);
        setPicked(Object.fromEntries((r.data || []).map((m) => [m.id, "ENG"])));
      })
      .catch(() => setManuals([]));
  }, []);

  if (!manuals.length) return null;

  const download = async (mid) => {
    setBusy("dl-" + mid);
    try { await downloadSpecialManual(mid, picked[mid] || "ENG"); }
    finally { setBusy(""); }
  };

  const view = async (m) => {
    const lang = picked[m.id] || "ENG";
    setBusy("view-" + m.id);
    try {
      const res = await client.get(`/manual/special/${m.id}.json`, { params: { lang } });
      setPreview(res.data);
    } finally { setBusy(""); }
  };

  return (
    <Card className="space-y-4" data-testid="special-manuals">
      <div className="flex flex-wrap items-center gap-2">
        <BookOpen size={18} className="text-[var(--primary)]" />
        <span className="font-head text-lg">Customer Support &amp; Master Enabler manuals</span>
      </div>
      <p className="text-sm opacity-70">
        Special manuals for support agents and administrators. Availability is role-based — administrator manuals are not shown to delegated seats.
      </p>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* left: manual list */}
        <div className="space-y-3">
          {manuals.map((m) => (
            <div key={m.id} className="rounded-lg border border-black/10 dark:border-white/10 p-3 space-y-2" data-testid={`special-manual-${m.id}`}>
              <div className="flex items-start gap-2">
                <FileText size={16} className="mt-0.5 text-[var(--primary)]" />
                <div className="min-w-0">
                  <div className="font-medium text-sm">{m.title}</div>
                  <div className="text-xs opacity-60">{m.subtitle}</div>
                </div>
                {m.audience === "admin"
                  ? <span className="ms-auto shrink-0"><Badge color="warning"><ShieldCheck size={11} className="inline" /> Admin</Badge></span>
                  : <span className="ms-auto shrink-0"><Badge color="info">Support</Badge></span>}
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <div className="w-40">
                  <Select label="Language" value={picked[m.id] || "ENG"}
                    onChange={(e) => setPicked({ ...picked, [m.id]: e.target.value })}
                    data-testid={`special-lang-${m.id}`}>
                    {m.languages.map((c) => <option key={c} value={c}>{`${c} · ${LANG_NATIVE[c] || c}`}</option>)}
                  </Select>
                </div>
                <Button variant="subtle" onClick={() => view(m)} disabled={busy === "view-" + m.id}
                  data-testid={`special-view-${m.id}`}>
                  <Eye size={15} /> {busy === "view-" + m.id ? "Loading…" : "View"}
                </Button>
                <Button variant="ghost" onClick={() => download(m.id)} disabled={busy === "dl-" + m.id}
                  data-testid={`special-download-${m.id}`}>
                  <Download size={15} /> {busy === "dl-" + m.id ? "Preparing…" : "PDF"}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* right: inline reading panel */}
        <div className="rounded-lg border border-black/10 dark:border-white/10 flex flex-col min-h-[440px] max-h-[720px]" data-testid="special-preview-panel">
          {preview ? (
            <>
              <div className="flex items-center gap-2 border-b border-black/10 dark:border-white/10 px-3 py-2">
                <FileText size={14} className="text-[var(--primary)]" />
                <span className="text-sm font-medium truncate">Preview · {preview.lang}</span>
                <button onClick={() => setPreview(null)} className="ms-auto opacity-60 hover:opacity-100" title="Close preview" data-testid="special-preview-close">
                  <X size={16} />
                </button>
              </div>
              <div className="overflow-y-auto p-4">
                <ManualBody data={preview} />
              </div>
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center opacity-60" data-testid="special-preview-empty">
              <Eye size={28} />
              <p className="text-sm">Select a manual and language, then click <b>View</b> to read it here. Use <b>PDF</b> to download.</p>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
